import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const resources = {
  en: {
    translation: {
      common: {
        appName: 'RavePal',
        festivalName: 'EDC Mexico 2026',
        noPerformancesFound: 'No performances found.',
        retry: 'Retry',
        back: 'Back',
        dismiss: 'Dismiss',
        website: 'Website',
        buyMeACoffee: 'Buy Me a Coffee',
        facebook: 'Facebook',
        offlineReady: 'Offline Ready',
      },
      nav: {
        home: 'Home',
        lineup: 'Lineup',
        favorites: 'Favorites',
        map: 'Map',
        settings: 'Settings',
      },
      schedule: {
        searchFavoritesPlaceholder: 'Search Favorites...',
        searchDjPlaceholder: 'Search DJ...',
        navigateToStage: 'Navigate to Stage',
        conflictsWith: 'Conflicts with: {{artists}}',
      },
      marquee: {
        liveNow: 'Live Now',
        showOnMap: 'Show on map',
        playingNow: 'Playing Now',
        nothingPlayingNow: 'Nothing playing now',
        upNextMins: 'Up Next ({{mins}} min)',
        upNextHoursMins: 'Up Next ({{hours}} hr{{minutesPart}})',
      },
      map: {
        stageMap: 'Stage Map',
        mapDataNotDownloaded: 'Map data not downloaded. Connect to the internet to download.',
        failedToLoadMap: 'Failed to load map.',
        loadingMap: 'Loading map...',
        recenterOnLocation: 'Recenter on my location',
        enableLocationForDistance: 'Enable location to see distance',
        playingNow: 'Playing Now',
        nothingPlayingNow: 'Nothing playing now',
        upNext: 'Up Next: {{dj}}{{timePart}}',
      },
      install: {
        installApp: 'Install {{appName}}',
        iosInstruction: 'Tap Share, then "Add to Home Screen"',
        androidInstruction: 'Add to your home screen for quick access',
        install: 'Install',
      },
      offline: {
        tipPrefix: 'For offline use: open this app from the home screen',
        tipEmphasis: 'once while connected',
        tipSuffix: ', then it will work in airplane mode.',
      },
      pages: {
        lineupTitle: 'Lineup',
        favoritesTitle: 'My Favorites',
      },
      notifications: {
        enabled: 'Notifications Enabled',
        enable: 'Enable Notifications',
        unavailable: 'Notifications not available (e.g. iOS Safari)',
        title: 'Upcoming Set: {{dj}}',
        body: '{{dj}} starts in {{minutes}} minutes at {{stage}}!',
      },
      time: {
        hoursMinutesShort: '{{hours}} hr{{minutesPart}}',
        minutesShort: '{{minutes}} min',
      },
      settings: {
        title: 'Settings',
        languageTitle: 'Language',
        languageDescription: 'Choose your preferred app language.',
        english: 'English',
        spanish: 'Spanish',
        timeSimulationTitle: 'Time Simulation',
        timeSimulationDescription: 'Override the current time to test the schedule logic.',
        virtualTimeLabel: 'Virtual Time',
        currentSystemTimeLabel: 'Current System Time:',
        resetToRealTime: 'Reset to Real Time',
        notificationsTitle: 'Notifications',
        notificationsDescription: 'Alert me before a favorite DJ starts.',
        howToUseTitle: 'How to use',
        howToUseDescription:
          'RavePal works as a website, but you will get the best experience if you install it as an app or add it to your home screen.',
        useTip1: 'First load must be done with internet so all schedule and map assets are cached.',
        useTip2:
          'After installing/adding to home screen, open it once online before heading to the festival.',
        useTip3:
          'Once cached, you can keep using lineup, favorites, and map even with weak or no signal.',
        aboutTitle: 'About',
        aboutParagraph1:
          'RavePal is an app built with AI assistance and my programming experience during an inspired night to help the rave and festival community navigate schedules, stages, and DJs offline (because signal at festivals is often weak).',
        aboutParagraph2:
          'I have more than 15 years of programming experience and built this for free. If you would like to support my work, you can do it on my Buy Me a Coffee page.',
        aboutParagraph3:
          'Thanks so much and have fun at the festival! If you see me around, feel free to say hi :)',
        facebookName: 'Serge Astro',
      },
    },
  },
  es: {
    translation: {
      common: {
        appName: 'RavePal',
        festivalName: 'EDC Mexico 2026',
        noPerformancesFound: 'No se encontraron presentaciones.',
        retry: 'Reintentar',
        back: 'Volver',
        dismiss: 'Cerrar',
        website: 'Sitio web',
        buyMeACoffee: 'Buy Me a Coffee',
        facebook: 'Facebook',
        offlineReady: 'Listo para usar sin internet',
      },
      nav: {
        home: 'Inicio',
        lineup: 'Lineup',
        favorites: 'Favoritos',
        map: 'Mapa',
        settings: 'Ajustes',
      },
      schedule: {
        searchFavoritesPlaceholder: 'Buscar en favoritos...',
        searchDjPlaceholder: 'Buscar DJ...',
        navigateToStage: 'Ir al escenario',
        conflictsWith: 'Se empalma con: {{artists}}',
      },
      marquee: {
        liveNow: 'En vivo ahora',
        showOnMap: 'Ver en mapa',
        playingNow: 'Sonando ahora',
        nothingPlayingNow: 'Nada sonando ahora',
        upNextMins: 'Sigue ({{mins}} min)',
        upNextHoursMins: 'Sigue ({{hours}} h{{minutesPart}})',
      },
      map: {
        stageMap: 'Mapa de escenarios',
        mapDataNotDownloaded: 'Los datos del mapa no se descargaron. Conectate a internet para descargarlos.',
        failedToLoadMap: 'No se pudo cargar el mapa.',
        loadingMap: 'Cargando mapa...',
        recenterOnLocation: 'Centrar en mi ubicacion',
        enableLocationForDistance: 'Activa tu ubicacion para ver distancia',
        playingNow: 'Sonando ahora',
        nothingPlayingNow: 'Nada sonando ahora',
        upNext: 'Sigue: {{dj}}{{timePart}}',
      },
      install: {
        installApp: 'Instalar {{appName}}',
        iosInstruction: 'Toca Compartir y luego "Agregar a pantalla de inicio"',
        androidInstruction: 'Agregalo a tu pantalla de inicio para acceso rapido',
        install: 'Instalar',
      },
      offline: {
        tipPrefix: 'Para usar sin internet: abre esta app desde la pantalla de inicio',
        tipEmphasis: 'una vez con conexion',
        tipSuffix: ', y despues funcionara en modo avion.',
      },
      pages: {
        lineupTitle: 'Lineup',
        favoritesTitle: 'Mis favoritos',
      },
      notifications: {
        enabled: 'Notificaciones activadas',
        enable: 'Activar notificaciones',
        unavailable: 'Notificaciones no disponibles (por ejemplo, iOS Safari)',
        title: 'Set proximo: {{dj}}',
        body: '{{dj}} empieza en {{minutes}} minutos en {{stage}}!',
      },
      time: {
        hoursMinutesShort: '{{hours}} h{{minutesPart}}',
        minutesShort: '{{minutes}} min',
      },
      settings: {
        title: 'Ajustes',
        languageTitle: 'Idioma',
        languageDescription: 'Elige tu idioma preferido para la app.',
        english: 'Ingles',
        spanish: 'Espanol',
        timeSimulationTitle: 'Simulacion de hora',
        timeSimulationDescription: 'Cambia la hora actual para probar la logica del horario.',
        virtualTimeLabel: 'Hora virtual',
        currentSystemTimeLabel: 'Hora actual del sistema:',
        resetToRealTime: 'Restablecer hora real',
        notificationsTitle: 'Notificaciones',
        notificationsDescription: 'Avisame antes de que empiece un DJ favorito.',
        howToUseTitle: 'Como usar',
        howToUseDescription:
          'RavePal funciona como sitio web, pero tendras mejor experiencia si lo instalas como app o lo agregas a tu pantalla de inicio.',
        useTip1:
          'La primera carga debe hacerse con internet para que se guarden el horario y los recursos del mapa.',
        useTip2:
          'Despues de instalarlo/agregarlo a inicio, abre la app una vez en linea antes de ir al festival.',
        useTip3:
          'Ya con cache, puedes seguir usando lineup, favoritos y mapa incluso con senal debil o sin senal.',
        aboutTitle: 'Acerca de',
        aboutParagraph1:
          'RavePal es una aplicacion desarrollada con ayuda de IA y mi experiencia de programacion en una noche de inspiracion para ayudar a la comunidad rave y festivalera a navegar horarios, escenarios y DJs de forma offline (porque en festivales la senal suele ser mala).',
        aboutParagraph2:
          'Soy programador con mas de 15 anos de experiencia y este trabajo fue gratuito. Si quieres apoyarme, puedes hacerlo en mi pagina de Buy Me a Coffee.',
        aboutParagraph3:
          'Muchas gracias y diviertete en el festival. Si me ves por ahi, no dudes en saludarme :)',
        facebookName: 'Serge Astro',
      },
    },
  },
} as const;

export function normalizeLanguage(lang?: string | null): SupportedLanguage {
  const lower = (lang ?? '').toLowerCase();
  if (lower.startsWith('es')) return 'es';
  return 'en';
}

function getStoredLanguage(): SupportedLanguage | null {
  if (typeof localStorage === 'undefined') return null;
  const raw =
    localStorage.getItem('ravepal_settings') ?? localStorage.getItem('edc_settings');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { language?: string };
    if (!parsed.language) return null;
    return normalizeLanguage(parsed.language);
  } catch {
    return null;
  }
}

if (!i18n.isInitialized) {
  const initialLanguage =
    getStoredLanguage() ??
    normalizeLanguage(typeof navigator !== 'undefined' ? navigator.language : 'en');
  i18n.use(initReactI18next).init({
    resources,
    lng: initialLanguage,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });
}

export default i18n;
